/**
 * key at 4dmanage@
 */
import { environment } from '../../../../environments/environment';
import { Component, Input, signal, effect, OnInit, AfterViewInit, input, OnDestroy, output } from '@angular/core';
import { APIService } from '../../../shared/api.service';

import mapboxgl from 'mapbox-gl';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.css'],
  standalone: false
})
export class MapComponent implements OnInit, AfterViewInit, OnDestroy {

  mapMarkers = signal<mapboxgl.Marker[]>([])
  mapId: string;
  map!: mapboxgl.Map;
  latlng: mapboxgl.LngLatLike = { lat: 52.9, lng: -1.18 };

  config: any = {};

  @Input()
  public set lines(lineSegments: IMapboxLine[]) {
    if (!lineSegments || lineSegments.length === 0) {
      this.updateLines([]); // Clear lines if null/empty
      return;
    }
    this.updateLines(lineSegments);
  }

  @Input()
  style = 'mapbox://styles/mapbox/streets-v12';

  @Input()
  public set markers(markers: mapboxgl.Marker[]) {
    if (!markers || markers.length === 0) {
      return;
    }
    const didUpdate = (this.mapMarkers()?.length > 0);

    // remove markers already on map
    if (didUpdate) {
      this.mapMarkers().forEach(marker => marker.remove());
    }

    this.mapMarkers.set(markers);
    if (didUpdate) {
      this.applyMarkersIfMap();
    }
  }

  @Input()
  dragPan = true;

  @Input()
  dragRotate = true;

  @Input()
  interactive = true;

  @Input()
  doubleClickZoom = true;

  @Input()
  zoom = 5.8;

  @Input()
  hasControls = true;

  @Input()
  public set isStatic(v: boolean) {
    if (typeof v !== "boolean") {
      return;
    }

    if (v) {
      this.dragPan = false;
      this.dragRotate = false;
      this.interactive = false;
      this.doubleClickZoom = false;
      this.hasControls = false;
    }

  }


  hoveredLineId = null;

  onMapClick = output<any>();

  showToolbar: boolean = false;

  _center!: mapboxgl.LngLatLike;

  @Input()
  public set center(latlng: mapboxgl.LngLatLike) {
    if (!latlng) {
      return;
    }
    this._center = latlng;
  }

  /**
   * To persist state pass the localstorage key
   */
  mapKey = input<string>();
  orgId!: number;


  features: any[] = [];

  constructor(private apiService: APIService) {

    this.mapId = crypto.randomUUID().replaceAll('-', '');

    console.log(this.mapId);
    effect(() => {
      if (this.mapMarkers()?.length) {
        if (!this.applyMarkersIfMap()) {
          setTimeout(() => {
            if (!this.applyMarkersIfMap()) {
              setTimeout(() => {
                this.applyMarkersIfMap();
              }, 1000);
            };
          }, 500);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    console.log('view init');
    (mapboxgl as any).accessToken = environment.mapbox.accessToken;
    try {
      this.orgId = this.apiService.getUserOrg().id;
      this.getLocalstorage();
    } catch (e) {
    }

    this.map = new mapboxgl.Map({
      container: this.mapId,
      style: this.style, // style URL
      zoom: this.config?.map?.zoom ?? this.zoom,
      center: this.config?.map?.center ?? this._center ?? this.latlng,
      dragPan: this.dragPan,
      dragRotate: this.dragRotate,
      doubleClickZoom: this.doubleClickZoom,
      interactive: this.interactive
    });

    if (this.interactive) {
      this.map.addInteraction('map-click', {
        type: 'click',
        handler: (e) => {
          console.log(e);

          let response: any = e;

          const features = this.map.queryRenderedFeatures(e.point, {
            layers: ['lines-layer']
          });

          if (features.length > 0) {
            console.log('Clicked line feature:', features[0]);
            this.onMapClick.emit({ lngLat: e.lngLat, feature: features[0] });
            response.feature = features[0];
          } else {
            console.log('Clicked at (no line):', e.lngLat.lng, e.lngLat.lat);
            this.onMapClick.emit({ lngLat: e.lngLat });
          }


          console.log(`Clicked at: ${e.lngLat.lng}, ${e.lngLat.lat}`);


          this.onMapClick.emit(response);
        }
      });
    }

    if (this.hasControls) {
      this.map.addControl(new mapboxgl.NavigationControl());
    }

    // Initialise line source and layer
    this.map.on('load', () => {

      if (this.features) {
        this.map.addSource('lines-source', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: this.features
          }
        });

        this.map.addLayer({
          id: 'lines-layer',
          type: 'line',
          source: 'lines-source',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {

            'line-color': [
              'case',
              ['boolean', ['feature-state', 'hover'], false], // Check hover state
              '#0226f1',
              '#888'
            ],
            'line-width': [
              'case',
              ['boolean', ['feature-state', 'hover'], false], // Check hover state
              4,
              2
            ]
          }
        });

        console.log('Lines layer added:', this.map.getLayer('lines-layer'));
        console.log('Setting line data:', this.features);
      }
    });

    this.map.on('mousemove', 'lines-layer', (e) => {
      if (!e?.features) return;

      if (e.features.length > 0) {
        if (this.hoveredLineId !== null) {
          this.map.setFeatureState(
            { source: 'lines-source', id: this.hoveredLineId },
            { hover: false }
          );
        }

        this.hoveredLineId = e.features[0]?.properties?.id;
        if (this.hoveredLineId) {
          this.map.setFeatureState(
            { source: 'lines-source', id: this.hoveredLineId },
            { hover: true }
          );
        }
      }
    });

    this.map.on('mouseleave', 'lines-layer', () => {
      if (this.hoveredLineId !== null) {
        this.map.setFeatureState(
          { source: 'lines-source', id: this.hoveredLineId },
          { hover: false }
        );
      }
      this.hoveredLineId = null;
    });
  }

  ngOnInit(): void {
    console.log(`oninit`, this.map);
  }

  private updateLines(lineSegments: IMapboxLine[]) {
    this.features = lineSegments.map((points, index) => ({
      type: 'Feature',
      id: points.id,
      properties: { id: points.id, title: points.title }, // Optional: unique ID for each line
      geometry: {
        type: 'LineString',
        coordinates: [[points.from.lng, points.from.lat], [points.to.lng, points.to.lat]]
      }
    }));
    //(this.map.getSource('lines-source') as mapboxgl.GeoJSONSource).setData(lineGeoJSON);
  }

  getLocalstorage() {
    try {
      if (this.mapKey()) {
        const configString = localStorage.getItem(`${this.getLocalstoragePreKey()}:${this.mapKey()}:config`);
        if (!configString) {
          return;
        }

        const map = JSON.parse(configString);

        this.config = map;
      }
    } catch (e) {
      // Ignore errors
    }
  }

  getConfigFromMap() {
    this.config = {
      zoom: this.map.getZoom(),
      center: this.map.getCenter()
    }
  }

  getLocalstoragePreKey() {
    return `org:${this.orgId}`
  }

  saveLocalStorage() {
    this.getConfigFromMap();
    try {
      if (this.mapKey()) {

        const payload = { map: this.config };
        localStorage.setItem(`${this.getLocalstoragePreKey()}:${this.mapKey()}:config`, JSON.stringify(payload));
      }
    } catch (e) {
      // Ignore errors
    }
  }

  applyMarkersIfMap() {
    if (this.map) {
      this.applyMarkers();
      console.log('applied markers');
      return true;
    } else {
      return false;
    }
  }

  applyMarkers() {
    for (const marker of this.mapMarkers()) {
      marker.addTo(this.map);
    }
  }

  clickSitesInAlarm() {
    this.apiService.getRulePackages('alarms-conditions').then(rp => {
      console.log(rp);
      const inAlert = rp.filter(rp => rp.inAlert === 'Y');

    });
  }

  ngOnDestroy(): void {
    this.saveLocalStorage();
  }

}


export interface IMapboxLine {
  id: number;
  title: string;
  from: { lat: number, lng: number };
  to: { lat: number, lng: number };
}
